import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ProbabilityService } from "../../../services/user/probability.services";
import { handleNotify, TOASTER_TYPE, TOASTER_POSITION } from "../../common/notification/toaster_notify.component";
import { useSelector } from "react-redux";
import Select from 'react-select';

const AddProbabilityComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const probabilityId = location.state?.probabilityId || 0;
    const isEditMode = !!probabilityId;
    const selectedStore = useSelector((state) => state?.user?.selectedStoreId);
    const [loading, setLoading] = useState(false);

    const probabilityTypeOptions = [
        { value: 'Invoice', label: 'Invoice' },
        { value: 'Quotes', label: 'Quotes' },
        { value: 'Leads', label: 'Leads' },
        { value: 'Activity', label: 'Activity' },
    ];

    const formik = useFormik({
        initialValues: {
            probabilityId: 0,
            probabilityName: "",
            probabilityType: "Invoice",
            isActive: true,
            storeId: selectedStore,
        },
        validationSchema: Yup.object({
            probabilityName: Yup.string().required("Please enter a valid name"),
        }),
        onSubmit: async (values) => {
            try {
                setLoading(true)
                const data = {
                    ...values,
                    isActive: values.isActive === true ? true : false
                };
                if (isEditMode) {
                    await ProbabilityService.updateProbabilityById(probabilityId, data)
                        .then((response) => {
                            if (response) {
                                navigate("/probability");
                            }
                        })
                } else {
                    await ProbabilityService.addNewProbability(data)
                        .then((response) => {
                            if (response) {
                                navigate("/probability");
                            }
                        });
                }
            } catch (error) {
                console.error("Error while submitting form:", error);
                handleNotify(
                    "An error occurred while saving the probability.",
                    TOASTER_TYPE.ERROR,
                    TOASTER_POSITION.TOP_RIGHT
                );
            } finally {
                setLoading(false);
            }
        },
    });

    useEffect(() => {
        if (isEditMode) {
            const fetchProbabilityDetails = async () => {
                try {
                    const data = await ProbabilityService.getProbabilityById(probabilityId);
                    formik.setValues({
                        probabilityId: data?.probabilityId,
                        probabilityName: data?.probabilityName,
                        probabilityType: data?.probabilityType,
                        isActive: data?.isActive,
                    });
                } catch (error) {
                    console.log("Error fetching probability details", error);
                    handleNotify(
                        "Error fetching probability details.",
                        TOASTER_TYPE.ERROR,
                        TOASTER_POSITION.TOP_RIGHT
                    );
                }
            };
            fetchProbabilityDetails();
        }
    }, []);

    const handleBackClick = () => {
        navigate("/probability");
    };

    return (
        <div className="page-wrapper m-0">
            <div className="page-content">
                <div className="page-breadcrumb d-flex align-items-center mb-3">
                    <div
                        className="breadcrumb-title pe-3 text-primary fw-bold fs-4 cursor-pointer"
                        onClick={handleBackClick}
                    >
                        Probabilities</div>
                    <div className="ps-3">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb mb-0 p-0">
                                <li className="breadcrumb-item active" aria-current="page">
                                    {isEditMode ? "Edit Probability" : "New Probability"}
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <div className="ms-auto">
                        <button
                            type="button"
                            className="btn btn-outline-secondary me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                            onClick={handleBackClick}
                        >
                            Back
                        </button>
                    </div>
                </div>

                <div className="card radius-10 border-top border-0 border-4 border-primary p-4">
                    <div className="card-body form-container">
                        <form onSubmit={formik.handleSubmit} className="mt-4">
                            {/* Name Field */}
                            <div className="row mb-4">
                                <div className="col-md-6 col-12">
                                    <label htmlFor="probabilityName" className="form-label d-flex">
                                        Name <span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="text"
                                        id="probabilityName"
                                        name="probabilityName"
                                        className={`form-control ${formik.touched.probabilityName && formik.errors.probabilityName ? "is-invalid" : ""}`}
                                        placeholder="Enter Probability Name"
                                        value={formik.values.probabilityName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.probabilityName && formik.errors.probabilityName && (
                                        <div className="invalid-feedback d-flex">{formik.errors.probabilityName}</div>
                                    )}
                                </div>
                            </div>

                            {/* Type Field (Dropdown) */}
                            <div className="row mb-4">
                                <div className="col-md-6 col-12">
                                    <label htmlFor="probabilityType" className="form-label d-flex">
                                        Type:
                                    </label>
                                    <div className="text-start">
                                        <Select
                                            id="probabilityType"
                                            name="probabilityType"
                                            options={probabilityTypeOptions}
                                            value={probabilityTypeOptions.find(option => option.value === formik.values.probabilityType)}
                                            onChange={selectedOption => formik.setFieldValue('probabilityType', selectedOption.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Status Radio Buttons */}
                            <div className="d-flex align-items-center mb-4">
                                <div className="form-check form-check-inline me-3">
                                    <input
                                        type="radio"
                                        id="active"
                                        name="isActive"
                                        className="form-check-input"
                                        value={true}
                                        onChange={() => formik.setFieldValue("isActive", true)}
                                        onBlur={formik.handleBlur}
                                        checked={formik.values.isActive === true}
                                    />
                                    <label className="form-check-label" htmlFor="active">
                                        Active
                                    </label>
                                </div>
                                <div className="form-check form-check-inline">
                                    <input
                                        type="radio"
                                        id="inactive"
                                        name="isActive"
                                        className="form-check-input"
                                        value={false}
                                        onChange={() => formik.setFieldValue("isActive", false)}
                                        onBlur={formik.handleBlur}
                                        checked={formik.values.isActive === false}
                                    />
                                    <label className="form-check-label" htmlFor="inactive">
                                        Inactive
                                    </label>
                                </div>
                            </div>

                            {/* Submit */}
                            <hr />
                            <div className="d-flex flex-column flex-md-row mt-4 justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary mb-2 mb-md-0 me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                                    onClick={handleBackClick}
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary px-4 py-2 rounded-3 shadow-sm hover-effect"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    ) : isEditMode ? "Update" : "Save"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddProbabilityComponent;
